import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { supplementProviderApi } from "apis";
import { useToast } from "contexts/ToastContext";
import {
  useCreateFormula,
  useGetFormulasInPrescription,
  useRemoveFormulaFromPrescription,
} from "hooks/useFormula";
import { FormulaInPrescriptionDto } from "libs/cellera-sdk";
import { useCallback, useEffect, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { CreateFormulaRequest, FORMULA_TYPE_MAP } from "types/formula";

import ConfirmDeleteModal from "./ConfirmDeleteModal";
import EmptyFormula from "./EmptyFormula";
import FormulaCreator from "./FormulaCreator";
import LoadingFormula from "./LoadingFormula";

const TABLE_HEADERS = [
  {
    label: "Formula Name",
    key: "name",
  },
  {
    label: "Dosage Type",
    key: "type",
  },
  {
    label: "Supply",
    key: "supply",
  },
  {
    label: "Price",
    key: "price",
  },
  {
    label: "Actions",
    key: "actions",
    width: "100px",
  },
];

interface FormulaProps {
  prescriptionId?: string;
  patientId: string;
  onFormulasChange?: (formulas: FormulaInPrescriptionDto[]) => void;
  onCreatePrescription?: () => Promise<string | undefined>;
  isSaving?: boolean;
  readOnly?: boolean;
}

function Formula({
  prescriptionId,
  patientId,
  onFormulasChange,
  onCreatePrescription,
  isSaving = false,
  readOnly = false,
}: FormulaProps) {
  const { colors } = useCelleraTheme();
  const { showError } = useToast();
  const [showFormulaCreator, setShowFormulaCreator] = useState(false);
  const [formulasInPrescription, setFormulasInPrescription] = useState<
    FormulaInPrescriptionDto[]
  >([]);
  const [formulaToDelete, setFormulaToDelete] =
    useState<FormulaInPrescriptionDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [currentPrescriptionId, setCurrentPrescriptionId] = useState<
    string | undefined
  >(prescriptionId);

  const queryClient = useQueryClient();

  const { data: formulasInPrescriptionData, isLoading } =
    useGetFormulasInPrescription({
      prescriptionId: currentPrescriptionId ?? "",
    });

  // Update currentPrescriptionId when prescriptionId prop changes
  useEffect(() => {
    if (prescriptionId) {
      setCurrentPrescriptionId(prescriptionId);
    }
  }, [prescriptionId]);

  const { mutateAsync: createFormula, isPending: isCreatingFormula } =
    useCreateFormula();

  const {
    mutateAsync: removeFormulaFromPrescription,
    isPending: isRemovingFormulaFromPrescription,
  } = useRemoveFormulaFromPrescription();

  const handleAddNewFormula = useCallback(() => {
    setShowFormulaCreator(true);
    setTimeout(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    }, 100);
  }, []);

  const handleCloseFormulaCreator = useCallback(() => {
    setShowFormulaCreator(false);
  }, []);

  const handleCreateFormula = useCallback(
    async (formula: CreateFormulaRequest) => {
      // Close the formula creator and show loading immediately
      setShowFormulaCreator(false);
      setIsCreating(true);

      let targetPrescriptionId = currentPrescriptionId;
      let optimisticFormula: FormulaInPrescriptionDto | null = null;
      let previousFormulas: FormulaInPrescriptionDto[] = [];

      try {
        // If no prescription exists, create one first
        if (!targetPrescriptionId && onCreatePrescription) {
          try {
            targetPrescriptionId = await onCreatePrescription();
            setCurrentPrescriptionId(targetPrescriptionId);
          } catch (error) {
            console.error("Failed to create prescription:", error);
            setIsCreating(false);
            throw error;
          }
        }

        // Create the formula
        const newFormula = await createFormula({
          ...formula,
          patientId: patientId,
        });

        // Create optimistic formula object
        optimisticFormula = {
          formulaId: newFormula.forid.toString(),
          name: newFormula.name,
          type: newFormula.type.toString(),
          quantity: newFormula.supply,
          price: newFormula.price,
        };

        // Optimistically add to local state immediately
        const formulaToAdd = optimisticFormula;
        if (formulaToAdd) {
          if (targetPrescriptionId) {
            // Save previous state for rollback
            previousFormulas = [...formulasInPrescription];
            setFormulasInPrescription((prev) => [formulaToAdd, ...prev]);
            setIsCreating(false); // Hide loading immediately
          } else {
            setFormulasInPrescription((prev) => [formulaToAdd, ...prev]);
            setIsCreating(false);
          }
        }

        // If we have a prescription, add the formula to it in the background
        if (targetPrescriptionId) {
          try {
            await supplementProviderApi.addFormulasToPrescription({
              prescriptionId: targetPrescriptionId,
              addFormulasRequestDto: {
                formulaIds: [newFormula.forid.toString()],
              },
            });
            // Refetch to ensure data is in sync
            queryClient.invalidateQueries({
              queryKey: ["formulasInPrescription"],
            });
          } catch (error) {
            // Rollback optimistic update on error
            setFormulasInPrescription(previousFormulas);
            console.error("Failed to add formula to prescription:", error);
            showError(
              "Add Formula Failed",
              error instanceof Error
                ? error.message
                : "Failed to add formula to prescription. Please try again."
            );
            throw error;
          }
        }
      } catch (error) {
        // Rollback optimistic update on error
        if (optimisticFormula) {
          if (targetPrescriptionId) {
            setFormulasInPrescription(previousFormulas);
          } else {
            const formulaIdToRemove = optimisticFormula.formulaId;
            setFormulasInPrescription((prev) =>
              prev.filter((f) => f.formulaId !== formulaIdToRemove)
            );
          }
        }
        setIsCreating(false);
        throw error;
      }
    },
    [
      createFormula,
      patientId,
      currentPrescriptionId,
      onCreatePrescription,
      queryClient,
      showError,
      formulasInPrescription,
    ]
  );

  const handleOpenDeleteModal = useCallback(
    (formula: FormulaInPrescriptionDto) => {
      setFormulaToDelete(formula);
    },
    []
  );

  const handleCloseDeleteModal = useCallback(() => {
    setFormulaToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!formulaToDelete) return;

    setIsDeleting(true);

    // Save previous state for rollback
    const previousFormulas = [...formulasInPrescription];
    const deletedFormula = formulaToDelete;

    // Optimistically remove from local state immediately
    setFormulasInPrescription((prev) =>
      prev.filter((f) => f.formulaId !== deletedFormula.formulaId)
    );
    handleCloseDeleteModal();
    setIsDeleting(false); // Hide loading immediately

    try {
      if (currentPrescriptionId) {
        await removeFormulaFromPrescription({
          formulaId: deletedFormula.formulaId,
          prescriptionId: currentPrescriptionId,
        });
        // Refetch to ensure data is in sync
        queryClient.invalidateQueries({
          queryKey: ["formulasInPrescription"],
        });
      }
    } catch (error) {
      // Rollback optimistic update on error
      setFormulasInPrescription(previousFormulas);
      console.error("Error deleting formula:", error);
      showError(
        "Delete Formula Failed",
        error instanceof Error
          ? error.message
          : "Failed to delete formula. Please try again."
      );
    }
  }, [
    formulaToDelete,
    currentPrescriptionId,
    queryClient,
    removeFormulaFromPrescription,
    handleCloseDeleteModal,
    formulasInPrescription,
    showError,
  ]);

  useEffect(() => {
    if (formulasInPrescriptionData) {
      setFormulasInPrescription(formulasInPrescriptionData.formulas);
    }
  }, [formulasInPrescriptionData]);

  useEffect(() => {
    if (onFormulasChange) {
      onFormulasChange(formulasInPrescription);
    }
  }, [formulasInPrescription, onFormulasChange]);

  return (
    <Box sx={{ width: "100%" }} className="space-y-5">
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border.main}`,
          borderRadius: 1,
          overflow: "hidden",
          maxHeight: "550px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer>
          <Table stickyHeader sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: colors.background.secondary,
                }}
              >
                {TABLE_HEADERS.map((header) => (
                  <TableCell
                    key={header.key}
                    sx={{
                      fontWeight: 600,
                      py: 1.5,
                      borderRight: `1px solid ${colors.border.main}`,
                      backgroundColor: colors.background.secondary,
                      width: header.width,
                    }}
                    align={header.key === "actions" ? "center" : "left"}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading || isCreating || isCreatingFormula ? (
                <LoadingFormula />
              ) : formulasInPrescription?.length === 0 ? (
                <EmptyFormula
                  onCreateFormula={handleAddNewFormula}
                  readOnly={readOnly}
                />
              ) : (
                formulasInPrescription?.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: colors.background.secondary,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        py: 1,
                        borderRight: `1px solid ${colors.border.main}`,
                      }}
                    >
                      {row.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        borderRight: `1px solid ${colors.border.main}`,
                      }}
                    >
                      {
                        FORMULA_TYPE_MAP[
                          row.type as unknown as keyof typeof FORMULA_TYPE_MAP
                        ]
                      }
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        borderRight: `1px solid ${colors.border.main}`,
                      }}
                    >
                      {row.quantity}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: 1,
                        borderRight: `1px solid ${colors.border.main}`,
                      }}
                    >
                      ${row.price}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1, width: "120px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeleteModal(row)}
                          disabled={
                            readOnly ||
                            isSaving ||
                            isDeleting ||
                            isRemovingFormulaFromPrescription
                          }
                          sx={{
                            color: colors.error[500],
                            padding: 0.5,
                            "&:hover": {
                              backgroundColor: colors.error[100],
                            },
                            "&:disabled": {
                              color: colors.text.secondary,
                              opacity: 0.5,
                            },
                          }}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!!formulasInPrescription?.length && !readOnly && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              borderTop: `1px solid ${colors.border.main}`,
              minHeight: "52px",
              height: "52px",
            }}
          >
            <Button
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddNewFormula}
              sx={{
                color: colors.secondary.main,
                textTransform: "none",
                fontWeight: 500,
                py: 0.5,
              }}
            >
              Add New Item
            </Button>
          </Box>
        )}
      </Paper>

      {showFormulaCreator && (
        <FormulaCreator
          onClose={handleCloseFormulaCreator}
          isCreating={isCreatingFormula}
          onCreate={handleCreateFormula}
        />
      )}

      <ConfirmDeleteModal
        open={!!formulaToDelete}
        formulaName={formulaToDelete?.name || ""}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting || isRemovingFormulaFromPrescription}
      />
    </Box>
  );
}

export default Formula;
